import jwt_decode from "jwt-decode";
import Cookies from "js-cookie";
import axios from "axios";

// ============================================
// AUTH_VERSION - Bump this for breaking changes
// ============================================
// When you deploy a breaking change that requires users to re-login,
// increment this number. All users with an older version will be
// automatically logged out within 20 seconds.
const AUTH_VERSION = 5; // Change to 2, 3, etc. for breaking changes

/**
 * Token Expiration Checker
 * Periodically checks if the JWT token has expired and automatically
 * redirects to login page if it has. Also checks AUTH_VERSION for
 * forced re-authentication on breaking changes.
 */
class TokenExpirationChecker {
  constructor(checkInterval = 20000) { // Default: check every 20 seconds
    this.checkInterval = checkInterval;
    this.intervalId = null;
  }

  /**
   * Start periodic token expiration checking
   */
  start() {
    // Clear any existing interval
    this.stop();

    // Check immediately on start
    this.checkTokenExpiration();

    // Set up periodic checking
    this.intervalId = setInterval(() => {
      this.checkTokenExpiration();
    }, this.checkInterval);

    console.log(`Token expiration checker started (checking every ${this.checkInterval / 1000} seconds)`);
  }

  /**
   * Stop periodic checking
   */
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log("Token expiration checker stopped");
    }
  }

  /**
   * Check if the current token has expired or if AUTH_VERSION requires re-login
   */
  checkTokenExpiration() {
    try {
      // Get token from cookie
      const token = Cookies.get("Biodit");

      // If no token exists but the checker is running, it means the session was lost
      // (token was deleted or expired). Redirect to login immediately.
      if (!token) {
        console.warn("Token is missing. Session lost. Redirecting to login...");
        this.clearAuthData();
        this.stop();
        window.location.href = "/login";
        return;
      }

      // Check AUTH_VERSION - force logout if app version is newer than stored version
      const storedVersion = localStorage.getItem('authVersion');
      if (storedVersion && parseInt(storedVersion) < AUTH_VERSION) {
        console.warn(`Auth version mismatch (stored: ${storedVersion}, required: ${AUTH_VERSION}). Forcing re-login...`);
        this.clearAuthData();
        this.stop();
        window.location.href = "/login";
        return;
      }
      // Store current version for new logins
      localStorage.setItem('authVersion', AUTH_VERSION);

      // Decode the token to get expiration time
      const decoded = jwt_decode(token);

      // Get current time in seconds (JWT exp is in seconds)
      const currentTime = Date.now() / 1000;

      // Check if token has expired
      if (decoded.exp < currentTime) {
        console.warn("Token has expired. Logging out and redirecting to login...");

        // Clear all authentication data
        this.clearAuthData();

        // Stop the checker
        this.stop();

        // Redirect to login page
        window.location.href = "/login";
      }
    } catch (error) {
      // If token is malformed or cannot be decoded, clear auth data and redirect
      console.error("Error checking token expiration:", error);
      this.clearAuthData();
      this.stop();
      window.location.href = "/login";
    }
  }

  /**
   * Clear all authentication tokens and session data
   */
  clearAuthData() {
    // Remove cookies
    Cookies.remove("Biodit");
    Cookies.remove("loc");

    // Remove localStorage items
    localStorage.removeItem("loc");
    localStorage.removeItem("session");
    localStorage.removeItem("level");
    localStorage.removeItem("authVersion");

    // Remove axios auth header
    delete axios.defaults.headers.common["Authorization"];
  }

  /**
   * Update the check interval (in milliseconds)
   */
  setCheckInterval(newInterval) {
    this.checkInterval = newInterval;
    if (this.intervalId) {
      // Restart with new interval
      this.start();
    }
  }
}

// Create and export a singleton instance
const tokenChecker = new TokenExpirationChecker(20000); // Check every 20 seconds

export default tokenChecker;
